import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from 'react-router-dom';
import yardsData from "../assets/yards.json";
import yardRegionsData from "../assets/yard-regions.json";
import { FaBatteryFull, FaBatteryThreeQuarters, FaBatteryHalf, FaBatteryQuarter, FaBatteryEmpty, FaPlug, FaWifi, FaSignal, FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaChartLine, FaBolt, FaFilter, FaTimes, FaRobot, FaThermometerHalf } from 'react-icons/fa';
import Select from "react-select";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import UemDeviceModal from "../components/UemDeviceModal";
import DeviceGraphModal from "../components/DeviceGraphModal";
import DiagnosticsModal from "../components/DiagnosticsModal";
import FleetDiagnosticsModal from "../components/FleetDiagnosticsModal";
import { getFriendlyErrorMessage } from "../utils/friendlyError";
import { usePortalAccess } from "../context/PortalAccessContext";

// Fit map bounds to an array of { location_latitude, location_longitude }
function SpeedTestHistoryFitBounds({ results }) {
  const map = useMap();
  const points = useMemo(
    () =>
      (results || [])
        .map((r) => [r.location_latitude, r.location_longitude])
        .filter((p) => p[0] != null && p[1] != null && !Number.isNaN(p[0]) && !Number.isNaN(p[1])),
    [results]
  );
  useEffect(() => {
    if (points.length === 0) return;
    try {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 16 });
    } catch (_) {}
  }, [map, points]);
  return null;
}

// Model to Friendly Name mapping
const modelToFriendlyName = {
  "google Pixel 8a": "Google Pixel 8a",
  "ZTE K83V": "K83V",
  "ZTE K88": "K88",
  "ZTE K92": "K92",
  "samsung SM-G970U": "Samsung Galaxy S10e",
  "samsung SM-G970U1": "Samsung Galaxy S10e",
  "samsung SM-G970F": "Samsung Galaxy S10e",
  "samsung SM-G970W": "Samsung Galaxy S10e",
  "samsung SM-S901U": "Samsung Galaxy S22",
  "samsung SM-S901U1": "Samsung Galaxy S22",
  "samsung SM-S901E": "Samsung Galaxy S22",
  "samsung SM-S901W": "Samsung Galaxy S22",
  "samsung SM-G960U": "Samsung Galaxy S9",
  "samsung SM-G960U1": "Samsung Galaxy S9",
  "samsung SM-G965U1": "Samsung Galaxy S9+",
  "samsung SM-G965U": "Samsung Galaxy S9+",
  "samsung SM-G715U1": "Samsung Galaxy XCover Pro",
  "Lenovo Lenovo TB-X6C6L": "Lenovo K10 Tab",
  "samsung SM-X218U": "Samsung Galaxy Tab A9+",
  "samsung SM-X818U": "Samsung Galaxy Tab S9+",
  "samsung SM-X828U": "Samsung Galaxy Tab S10+"
};

const getFriendlyName = (model) => {
  if (!model) return "-";
  if (modelToFriendlyName[model]) {
    return modelToFriendlyName[model];
  }
  const lowerModel = model.toLowerCase();
  for (const [key, value] of Object.entries(modelToFriendlyName)) {
    if (key.toLowerCase() === lowerModel) {
      return value;
    }
  }
  return model;
};

/** `/maiApi/latest-android-data` may return a bare array or a wrapped payload. */
function normalizeLatestAndroidRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload == null) return [];
  if (typeof payload === 'object') {
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.rows)) return payload.rows;
    if (Array.isArray(payload.items)) return payload.items;
  }
  return [];
}

const celsiusToFahrenheit = (celsius) => {
  if (celsius == null) return "-";
  return Math.round((celsius * 9/5) + 32);
};

const getBatteryIcon = (percent, isCharging) => {
  if (isCharging) return <FaPlug className="text-green-500" />;
  if (percent >= 80) return <FaBatteryFull className="text-green-500" />;
  if (percent >= 60) return <FaBatteryThreeQuarters className="text-green-500" />;
  if (percent >= 40) return <FaBatteryHalf className="text-yellow-500" />;
  if (percent >= 20) return <FaBatteryQuarter className="text-orange-500" />;
  return <FaBatteryEmpty className="text-red-500" />;
};

const getSignalStrength = (signal) => {
  if (signal == null) return { bars: 0, colorClass: "text-gray-500" };
  if (signal >= -70) return { bars: 4, colorClass: "text-green-500" };
  if (signal >= -80) return { bars: 3, colorClass: "text-yellow-500" };
  if (signal >= -90) return { bars: 2, colorClass: "text-orange-500" };
  return { bars: 1, colorClass: "text-red-500" };
};

const getDownloadColor = (download) => {
  if (download == null) return "text-gray-500";
  if (download >= 7) return "text-green-600";
  if (download >= 4) return "text-yellow-600";
  return "text-red-600";
};

const getUploadColor = (upload) => {
  if (upload == null) return "text-gray-500";
  if (upload >= 7) return "text-green-600";
  if (upload >= 4) return "text-yellow-600";
  return "text-red-600";
};

const getBatteryTempColor = (tempFahrenheit) => {
  if (tempFahrenheit == null) return "text-gray-500";
  // Very cold (<32°F) or very hot (>113°F) - Red (danger)
  if (tempFahrenheit < 32 || tempFahrenheit > 113) return "text-red-600";
  // Warm (95-113°F) - Yellow (caution)
  if (tempFahrenheit >= 95) return "text-yellow-600";
  // Normal (32-95°F) - Green (good)
  return "text-green-600";
};

export default function DeviceInfoCompact() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { access } = usePortalAccess();
  const portalEmail = (searchParams.get('portal_email') || access?.email || '').trim();
  const portalScope = (searchParams.get('portal_scope') || access?.scope || 'GLOBAL').trim();
  const [deviceData, setDeviceData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState('all'); // 'all' | 'name_id' | 'yard'
  const [chargingFilter, setChargingFilter] = useState([]);
  const [divisionFilter, setDivisionFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);

  const [networkFilter, setNetworkFilter] = useState(''); // '' | 'WiFi' | 'Cellular'
  const [cellularTypeFilter, setCellularTypeFilter] = useState([]); // e.g. ['LTE', '5G']

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filterBarCollapsed, setFilterBarCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState(''); // 'download-low', 'download-high', 'upload-low', 'upload-high'
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null); // { uuid, deviceFriendlyName }
  const [graphModal, setGraphModal] = useState({ isOpen: false, androidId: null, deviceName: null, graphType: null });
  const [diagnosticsModal, setDiagnosticsModal] = useState({ isOpen: false, androidId: null, deviceName: null });
  const [fleetDiagnosticsOpen, setFleetDiagnosticsOpen] = useState(false);
  const [speedTestStatus, setSpeedTestStatus] = useState({}); // { androidId: { requestId, status, loading } }
  const [lastSpeedTestTimestamps, setLastSpeedTestTimestamps] = useState({}); // { androidId: { last_speed_test_timestamp, next_speed_test_time, missed_test } }
  const [speedTestResultsModal, setSpeedTestResultsModal] = useState({ 
    isOpen: false, 
    androidId: null, 
    deviceFriendlyName: null,
    results: null, 
    schedule: null,
    loading: false,
    status: null,
    error: null
  });
  const [uemDataByDeviceId, setUemDataByDeviceId] = useState({}); // { androidId: { serial_number, imei, ... } }
  const [uemLoadingByDeviceId, setUemLoadingByDeviceId] = useState({}); // { androidId: true/false }
  const [manualSpeedTestEnabled, setManualSpeedTestEnabled] = useState(true);
  const [appButtonEnabledByDevice, setAppButtonEnabledByDevice] = useState({}); // { androidId: true/false }
  const [appButtonLoadingByDevice, setAppButtonLoadingByDevice] = useState({}); // { androidId: true/false }
  const [speedTestMapModal, setSpeedTestMapModal] = useState({
    open: false,
    lat: null,
    lng: null,
    uploadMbps: null,
    downloadMbps: null,
    signalDbm: null,
    label: '',
  });
  const [speedTestHistoryModal, setSpeedTestHistoryModal] = useState({
    open: false,
    androidId: null,
    deviceName: null,
    results: [],
    loading: false,
  });
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_SERVER_URL;
  const pollTimeoutRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  // Fetch global speed test settings once so we can hide manual trigger button when disabled
  useEffect(() => {
    if (!API_BASE) return;
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_BASE}/maiApi/speed-test/settings`);
        const enabled = res?.data?.settings?.manual_enabled;
        setManualSpeedTestEnabled(enabled !== false);
      } catch (e) {
        console.warn('[SPEED_TEST] Could not fetch initial settings:', e?.message);
      }
    };
    fetchSettings();
  }, [API_BASE, portalEmail]);

  // Fetch speed test history with GPS when history modal opens
  useEffect(() => {
    if (!speedTestHistoryModal.open || !speedTestHistoryModal.androidId || !API_BASE) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/maiApi/devices/${speedTestHistoryModal.androidId}/speed-test/history`,
          { params: { limit: 50 } }
        );
        if (!cancelled) {
          setSpeedTestHistoryModal((prev) => ({
            ...prev,
            results: res?.data?.results ?? [],
            loading: false,
          }));
        }
      } catch (e) {
        if (!cancelled) {
          setSpeedTestHistoryModal((prev) => ({ ...prev, results: [], loading: false }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [speedTestHistoryModal.open, speedTestHistoryModal.androidId, API_BASE]);

  // Read yard filter from URL parameter on mount or when URL changes (unified search)
  useEffect(() => {
    const yardParam = searchParams.get('yard');
    if (yardParam) {
      setSearchScope('yard');
      setSearchQuery(String(yardParam));
    }
  }, [searchParams]);

  // Get unique divisions from yard-regions.json, including BluCar
  const divisions = useMemo(() => {
    const divisionSet = new Set();
    Object.values(yardRegionsData).forEach(yard => {
      if (yard.division) {
        divisionSet.add(yard.division);
      }
      // Add BluCar as a division if yard has blucar flag
      if (yard.blucar) {
        divisionSet.add("BluCar");
      }
    });
    return Array.from(divisionSet).sort();
  }, []);

  // Get regions filtered by selected division
  const regions = useMemo(() => {
    if (!divisionFilter) return [];
    
    // Special handling for BluCar division
    if (divisionFilter === "BluCar") {
      return ["BluCar"]; // BluCar is both division and region
    }
    
    const regionSet = new Set();
    Object.values(yardRegionsData).forEach(yard => {
      // Only include regions that match the division, and exclude BluCar unless it's the selected division
      if (yard.division === divisionFilter && yard.region && yard.region !== "BluCar") {
        regionSet.add(yard.region);
      }
    });
    return Array.from(regionSet).sort();
  }, [divisionFilter]);


  // Reset region when division changes; clear yard search when division/region change
  useEffect(() => {
    if (divisionFilter) {
      setRegionFilter('');
      setSearchQuery((prev) => (searchScope === 'yard' ? '' : prev));
    }
  }, [divisionFilter]);

  useEffect(() => {
    if (regionFilter) {
      setSearchQuery((prev) => (searchScope === 'yard' ? '' : prev));
    }
  }, [regionFilter]);

  const isDeviceOffline = (item) => {
    if (!item.timestamp) return true;
    try {
      const now = new Date();
      const lastSeen = new Date(item.timestamp);
      if (isNaN(lastSeen.getTime())) return true;
      const diffMs = now.getTime() - lastSeen.getTime();
      if (diffMs <= 0) return false;
      const diffMins = diffMs / 60000;
      return diffMins > 30;
    } catch (e) {
      return true;
    }
  };

  const fetchLatestData = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      // Fetch device data (now includes last_speed_test_timestamp from backend join)
      const deviceRes = await axios.get(`${API_BASE}/maiApi/latest-android-data`, {
        params: {
          ...(portalEmail ? { portal_email: portalEmail } : {})
        }
      });
      
      const newData = normalizeLatestAndroidRows(deviceRes.data);
      
      // Deduplicate by android_id only, keeping the latest timestamp record per device
      const deviceMap = new Map();
      newData.forEach(item => {
        const existing = deviceMap.get(item.android_id);
        if (!existing || new Date(item.timestamp) > new Date(existing.timestamp)) {
          deviceMap.set(item.android_id, item);
        }
      });
      
      const uniqueDevices = Array.from(deviceMap.values());
      
      // Fetch speed test timestamps and schedules separately
      const timestamps = {};
      uniqueDevices.forEach(device => {
        timestamps[device.android_id] = {
          last_speed_test_timestamp: null, // Will be fetched from schedules endpoint
          next_speed_test_time: null, // Will be fetched from schedules
          missed_test: false
        };
      });
      
      // Fetch speed test timestamps and schedules (browser console: connection vs empty DB)
      let schedules = {};
      try {
        const schedulesRes = await axios.get(
          `${API_BASE}/maiApi/devices/last-speed-test-timestamps`
        );
        schedules = schedulesRes.data || {};
        console.log(
          '[MAI Speed test] Backend OK — GET /maiApi/devices/last-speed-test-timestamps succeeded (API reached; DB query completed on server).'
        );
        const scheduleIds = Object.keys(schedules);
        if (scheduleIds.length === 0) {
          console.log(
            '[MAI Speed test] No data: response was empty — no devices returned speed-test schedule rows. WiFi/Cell speed columns may stay blank.'
          );
        } else {
          console.log(
            `[MAI Speed test] Loaded schedule/speed-test fields for ${scheduleIds.length} device id(s).`
          );
        }
      } catch (err) {
        console.warn(
          '[MAI Speed test] GET last-speed-test-timestamps failed (network, 4xx/5xx, or server down):',
          err?.message || err
        );
      }

      // Merge schedule data (last_speed_test_timestamp, next_speed_test_time, missed_test, speed test results)
      Object.keys(schedules).forEach(androidId => {
        if (timestamps[androidId]) {
          timestamps[androidId].last_speed_test_timestamp = schedules[androidId].last_speed_test_timestamp || null;
          timestamps[androidId].next_speed_test_time = schedules[androidId].next_speed_test_time;
          timestamps[androidId].missed_test = schedules[androidId].missed_test || false;
          // Include speed test results for display (backward compatibility)
          timestamps[androidId].download_mbps = schedules[androidId].download_mbps || null;
          timestamps[androidId].upload_mbps = schedules[androidId].upload_mbps || null;
          timestamps[androidId].ping_ms = schedules[androidId].ping_ms || null;
          // Include connection info
          timestamps[androidId].connection_type = schedules[androidId].connection_type || null;
          timestamps[androidId].carrier_name = schedules[androidId].carrier_name || null;
          // Include separate WiFi and Cellular speed test results
          timestamps[androidId].wifi_speed_test = schedules[androidId].wifi_speed_test || null;
          timestamps[androidId].cellular_speed_test = schedules[androidId].cellular_speed_test || null;
        } else {
          // Device not in telemetry yet, but has schedule
          timestamps[androidId] = {
            last_speed_test_timestamp: schedules[androidId].last_speed_test_timestamp || null,
            next_speed_test_time: schedules[androidId].next_speed_test_time,
            missed_test: schedules[androidId].missed_test || false,
            download_mbps: schedules[androidId].download_mbps || null,
            upload_mbps: schedules[androidId].upload_mbps || null,
            ping_ms: schedules[androidId].ping_ms || null,
            // Include connection info
            connection_type: schedules[androidId].connection_type || null,
            carrier_name: schedules[androidId].carrier_name || null,
            // Include separate WiFi and Cellular speed test results
            wifi_speed_test: schedules[androidId].wifi_speed_test || null,
            cellular_speed_test: schedules[androidId].cellular_speed_test || null
          };
        }
      });
      
      // Merge speed test results into device data for display
      // Always merge connection info if available, even if speed test metrics are missing
      const updatedDeviceData = uniqueDevices.map(device => {
        const speedTestData = timestamps[device.android_id];
        
        // Always merge WiFi and Cellular speed test results if they exist, regardless of other fields
        const hasWifiTest = speedTestData?.wifi_speed_test != null;
        const hasCellularTest = speedTestData?.cellular_speed_test != null;
        
        // Merge if we have speed test data (either metrics OR connection info OR separate WiFi/Cellular tests)
        if (speedTestData && (
          speedTestData.download_mbps || 
          speedTestData.upload_mbps || 
          speedTestData.connection_type || 
          speedTestData.carrier_name ||
          speedTestData.ping_ms ||
          speedTestData.jitter_ms ||
          hasWifiTest ||
          hasCellularTest
        )) {
          const merged = {
            ...device,
            download_mbps: speedTestData.download_mbps || device.download_mbps || 0,
            upload_mbps: speedTestData.upload_mbps || device.upload_mbps || 0,
            ping_ms: speedTestData.ping_ms || device.ping_ms || null,
            // Always merge connection info if available (backward compatible - preserves existing values if new ones are null)
            speedtest_connection_type: speedTestData.connection_type || device.speedtest_connection_type || null,
            speedtest_carrier_name: speedTestData.carrier_name || device.speedtest_carrier_name || null,
            // Always include separate WiFi and Cellular speed test results (even if null)
            wifi_speed_test: speedTestData.wifi_speed_test || null,
            cellular_speed_test: speedTestData.cellular_speed_test || null
          };
          
          return merged;
        }
        
        // Even if no speed test data in timestamps, still merge WiFi/Cellular tests if they exist
        if (hasWifiTest || hasCellularTest) {
          return {
            ...device,
            wifi_speed_test: speedTestData.wifi_speed_test || null,
            cellular_speed_test: speedTestData.cellular_speed_test || null
          };
        }
        
        return device;
      });
      setDeviceData(updatedDeviceData);
      
      setLastSpeedTestTimestamps(timestamps);
    } catch (error) {
      console.error("Failed to fetch device data:", error);
      setDeviceData([]);
      setFetchError('Failed to load device data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchLatestData();
    const interval = setInterval(fetchLatestData, 120000); // Refresh every 2 minutes
    return () => {
      clearInterval(interval);
      clearTimeout(pollTimeoutRef.current);
      clearTimeout(refreshTimeoutRef.current);
    };
  }, [fetchLatestData]);

  // Trigger manual speed test
  const triggerSpeedTest = async (androidId) => {
    if (!API_BASE) return;

    // Check kill switch first: do not open modal or trigger if manual speed test is disabled
    try {
      const settingsRes = await axios.get(`${API_BASE}/maiApi/speed-test/settings`);
      const manualEnabled = settingsRes?.data?.settings?.manual_enabled;
      setManualSpeedTestEnabled(manualEnabled !== false);
      if (manualEnabled === false) {
        alert('Manual speed test is disabled. Enable it in the Speed Test settings (gear icon) to run a test.');
        return;
      }
    } catch (e) {
      console.warn('[SPEED_TEST] Could not fetch settings:', e?.message);
      // Proceed anyway if settings endpoint fails (e.g. old backend)
    }

    console.log(`[SPEED_TEST] Manual speed test triggered for device ${androidId}`);

    // Find device friendly name from deviceData
    const device = deviceData.find(d => d.android_id === androidId);
    const deviceFriendlyName = device?.device_friendly_name || device?.asset_tag || androidId;

    // Show popup immediately with loading state
    setSpeedTestResultsModal({
      isOpen: true,
      androidId: androidId,
      deviceFriendlyName: deviceFriendlyName,
      results: null,
      schedule: null,
      loading: true,
      status: 'requesting'
    });

    try {
      setSpeedTestStatus(prev => ({
        ...prev,
        [androidId]: { loading: true, status: 'requesting', requestId: null }
      }));

      const url = `${API_BASE}/maiApi/devices/${androidId}/speed-test/trigger`;
      console.log(`[SPEED_TEST] POST ${url}`);
      const response = await axios.post(url, {
        requested_by: 'user' // Could be user email if available
      });

      const requestId = response.data.request_id || response.data.requestId; // Backend returns request_id (snake_case)

      if (!requestId) {
        console.error('[SPEED_TEST] No request_id in backend response:', response.data);
        throw new Error('No request_id in response');
      }

      console.log(`[SPEED_TEST] Command queued — request_id=${requestId}. Device will poll GET /commands; polling status every 5s.`);
      setSpeedTestStatus(prev => ({
        ...prev,
        [androidId]: { loading: true, status: 'pending', requestId }
      }));

      // Update modal to show waiting for device
      setSpeedTestResultsModal(prev => ({
        ...prev,
        status: 'pending',
        loading: true
      }));

      // Poll for status
      pollSpeedTestStatus(androidId, requestId);
    } catch (error) {
      console.error('[SPEED_TEST] Trigger failed:', error.response?.data || error.message, error);
      setSpeedTestStatus(prev => ({
        ...prev,
        [androidId]: { loading: false, status: 'error', error: error.response?.data?.error || 'Failed to trigger speed test' }
      }));
      
      // Update modal to show error
      setSpeedTestResultsModal(prev => ({
        ...prev,
        loading: false,
        status: 'error',
        error: error.response?.data?.error || 'Failed to trigger speed test'
      }));
    }
  };

  // Toggle app speed test button enabled/disabled for a device
  const toggleAppSpeedTestButton = async (androidId) => {
    if (!API_BASE) return;

    const currentState = appButtonEnabledByDevice[androidId] !== false; // Default true if not set
    const newState = !currentState;

    setAppButtonLoadingByDevice(prev => ({ ...prev, [androidId]: true }));

    try {
      await axios.put(`${API_BASE}/maiApi/devices/${androidId}/app-speedtest-button`, {
        enabled: newState
      });

      setAppButtonEnabledByDevice(prev => ({ ...prev, [androidId]: newState }));
      console.log(`[APP_BUTTON] Toggled app button for ${androidId}: ${newState}`);
    } catch (error) {
      console.error('[APP_BUTTON] Failed to toggle app button:', error);
      alert(getFriendlyErrorMessage(error.response?.data?.error, 'Failed to update app button state.'));
    } finally {
      setAppButtonLoadingByDevice(prev => ({ ...prev, [androidId]: false }));
    }
  };

  // Fetch app button enabled state for a device
  const fetchAppButtonState = async (androidId) => {
    if (!API_BASE) return;

    try {
      const response = await axios.get(`${API_BASE}/maiApi/devices/${androidId}/app-speedtest-button`);
      const enabled = response.data?.app_button_enabled !== false;
      setAppButtonEnabledByDevice(prev => ({ ...prev, [androidId]: enabled }));
    } catch (error) {
      console.warn('[APP_BUTTON] Could not fetch app button state:', error?.message);
    }
  };

  // Fetch UEM data for device
  const fetchUemData = async (androidId) => {
    setUemLoadingByDeviceId(prev => ({ ...prev, [androidId]: true }));
    
    try {
      const response = await axios.get(`${API_BASE}/maiApi/devices/${androidId}/uem-data`);

      if (response.data.success) {
        setUemDataByDeviceId(prev => ({
          ...prev,
          [androidId]: response.data.data
        }));
      } else {
        alert(getFriendlyErrorMessage(response.data.error, 'Failed to fetch UEM data.'));
      }
    } catch (error) {
      console.error('Failed to fetch UEM data:', error);
      alert(getFriendlyErrorMessage(error.response?.data?.error, 'Failed to fetch UEM data.'));
    } finally {
      setUemLoadingByDeviceId(prev => ({ ...prev, [androidId]: false }));
    }
  };

  // Poll for speed test status.
  // errorCount tracks consecutive non-404 failures; after 3 the poll goes terminal
  // and cancels the per-device lock so group tests aren't blocked.
  const pollSpeedTestStatus = async (androidId, requestId, attempts = 0, errorCount = 0) => {
    // Validate requestId before polling
    if (!requestId || requestId === 'undefined' || requestId === 'null') {
      console.error('[SPEED_TEST] Invalid requestId, stopping poll:', requestId);
      setSpeedTestStatus(prev => ({
        ...prev,
        [androidId]: { loading: false, status: 'error', error: 'Invalid request ID' }
      }));
      return;
    }

    if (attempts > 120) { // Stop after 120 attempts (10 minutes)
      console.warn(`[SPEED_TEST] Timeout for ${androidId} after 120 polls (~10 min). Device may be offline, not polling /commands, or has no UUID.`);
      setSpeedTestStatus(prev => ({
        ...prev,
        [androidId]: { loading: false, status: 'timeout', requestId }
      }));
      setSpeedTestResultsModal(prev => ({
        ...prev,
        loading: false,
        status: 'timeout',
        error: 'Speed test timed out. Device may be offline or not responding.'
      }));
      return;
    }

    try {
      const response = await axios.get(`${API_BASE}/maiApi/devices/${androidId}/speed-test/status/${requestId}`);
      const status = response.data.status;

      if (attempts === 0 || status !== 'pending') {
        console.log(`[SPEED_TEST] Status poll #${attempts + 1} for ${androidId}: ${status}`);
      }

      if (status === 'completed' || status === 'failed' || status === 'expired') {
        setSpeedTestStatus(prev => ({
          ...prev,
          [androidId]: { loading: false, status, requestId }
        }));
        
        // If completed but results not yet written (race condition), keep polling briefly
        if (status === 'completed' && !response.data.results && attempts < 72) {
          console.warn(`[SPEED_TEST] Completed but no results yet for ${androidId}, retrying (attempt ${attempts + 1})...`);
          pollTimeoutRef.current = setTimeout(() => pollSpeedTestStatus(androidId, requestId, attempts + 1, 0), 5000);
          return;
        }

        // If completed, fetch results and schedule, then update modal
        if (status === 'completed' && response.data.results) {
          console.log(`[SPEED_TEST] Completed for ${androidId}:`, response.data.results);
          // Fetch schedule
          try {
            const scheduleResponse = await axios.get(`${API_BASE}/maiApi/devices/${androidId}/speed-test/schedule`);
            setSpeedTestResultsModal(prev => ({
              ...prev,
              results: response.data.results,
              schedule: scheduleResponse.data,
              loading: false,
              status: 'completed'
            }));
          } catch (err) {
            console.error('Failed to fetch schedule:', err);
            setSpeedTestResultsModal(prev => ({
              ...prev,
              results: response.data.results,
              schedule: null,
              loading: false,
              status: 'completed'
            }));
          }
        } else if (status === 'completed' && !response.data.results) {
          // Retries exhausted, completed but results never arrived
          console.error(`[SPEED_TEST] Completed but results never available for ${androidId}`);
          setSpeedTestResultsModal(prev => ({
            ...prev,
            loading: false,
            status: 'error',
            error: 'Speed test completed but results are unavailable. Please refresh the page to view results.'
          }));
        } else if (status === 'failed' || status === 'expired') {
          console.log(`[SPEED_TEST] Ended for ${androidId}: ${status}`);
          setSpeedTestResultsModal(prev => ({
            ...prev,
            loading: false,
            status: status,
            error: status === 'failed' ? 'Speed test failed' : 'Speed test expired'
          }));
        }
        
        // Refresh device data to show new results
        refreshTimeoutRef.current = setTimeout(() => fetchLatestData(), 2000);
      } else {
        // Still pending/acknowledged, poll again
        // Update modal status
        setSpeedTestResultsModal(prev => {
          if (prev.isOpen && prev.androidId === androidId) {
            return {
              ...prev,
              status: status,
              loading: true
            };
          }
          return prev;
        });
        
        pollTimeoutRef.current = setTimeout(() => pollSpeedTestStatus(androidId, requestId, attempts + 1, 0), 5000);
        setSpeedTestStatus(prev => ({
          ...prev,
          [androidId]: { loading: true, status, requestId }
        }));
      }
    } catch (error) {
      const httpStatus = error.response?.status;
      console.error('[SPEED_TEST] Status poll error:', httpStatus, error.response?.data || error.message);

      const is404 = httpStatus === 404;
      const isTerminalError = is404 || !requestId || requestId === 'undefined';
      const newErrorCount = isTerminalError ? 3 : errorCount + 1;
      const MAX_CONSECUTIVE_ERRORS = 3;

      if (!isTerminalError && newErrorCount < MAX_CONSECUTIVE_ERRORS) {
        // Transient error (e.g. brief network blip): retry up to MAX_CONSECUTIVE_ERRORS times.
        console.warn(`[SPEED_TEST] Transient poll error for ${androidId} (${newErrorCount}/${MAX_CONSECUTIVE_ERRORS}), retrying...`);
        pollTimeoutRef.current = setTimeout(() => pollSpeedTestStatus(androidId, requestId, attempts + 1, newErrorCount), 5000);
      } else {
        // Terminal: 404, invalid requestId, or too many consecutive failures (incl. 500).
        const errorMsg = is404
          ? 'Request not found'
          : 'Speed test status check failed. The test may not have run.';
        console.warn(`[SPEED_TEST] Stopping poll for ${androidId} — terminal error (status=${httpStatus}, consecutive=${newErrorCount}).`);
        setSpeedTestStatus(prev => ({
          ...prev,
          [androidId]: { loading: false, status: 'error', error: errorMsg }
        }));
        setSpeedTestResultsModal(prev => ({
          ...prev,
          loading: false,
          status: 'error',
          error: errorMsg
        }));
        // Release the per-device command lock so group tests aren't blocked.
        if (!is404) {
          axios.post(`${API_BASE}/maiApi/devices/${androidId}/speed-test/cancel`).catch(e =>
            console.warn('[SPEED_TEST] cancel request failed (non-critical):', e.message)
          );
        }
      }
    }
  };

  const getNetworkType = (item) => {
    if (item.apn_name) {
      const apn = String(item.apn_name).toLowerCase();
      if (apn.startsWith('wlan')) return 'WiFi';
      if (apn.startsWith('rmnet') || apn.includes('data')) return 'Cellular';
    }
    if (item.ssid && item.ssid !== 'N/A' && item.ssid !== '' && item.ssid !== 'Unknown') return 'WiFi';
    if (item.carrier && item.carrier !== 'Unknown' && item.carrier !== '' && item.carrier.trim() !== '') return 'Cellular';
    return 'Unknown';
  };

  const filteredData = useMemo(() => {
    let filtered = deviceData
      .filter(item => {
        // Unified search: scope 'all' | 'name_id' | 'yard'
        if (!searchQuery.trim()) return true;
        const query = searchQuery.trim().toLowerCase();
        const yardMatch = String(item.yard_number) === String(searchQuery.trim());
        if (searchScope === 'yard') {
          return yardMatch;
        }
        const nameIdMatch = (
          item.android_id?.toLowerCase().startsWith(query) ||
          item.device_name?.toLowerCase().includes(query) ||
          item.device_friendly_name?.toLowerCase().includes(query)
        );
        if (searchScope === 'name_id') return nameIdMatch;
        return nameIdMatch || yardMatch;
      })
      .filter(item => {
        // Charge state filter - radio button style (single select)
        if (chargingFilter.length === 0) return true;
        const chargingState = (item.charging_state || item.ac || '').toString().toLowerCase();
        const isCharging = chargingState === 'charging' || chargingState === 'ac' || chargingState === 'true';
        
        // If only 'Charging' selected, show only charging devices
        if (chargingFilter.length === 1 && chargingFilter.includes('Charging')) {
          return isCharging;
        }
        // If only 'Discharging' selected, show only discharging devices
        if (chargingFilter.length === 1 && chargingFilter.includes('Discharging')) {
          return !isCharging;
        }
        return true;
      })
      .filter(item => {
        const yardInfo = yardRegionsData[String(item.yard_number)];
        
        // Filter by division
        if (divisionFilter) {
          // Special handling for BluCar division
          if (divisionFilter === "BluCar") {
            if (!yardInfo || !yardInfo.blucar) {
              return false;
            }
          } else {
            if (!yardInfo || yardInfo.division !== divisionFilter) {
              return false;
            }
          }
        }
        
        // Filter by region
        if (regionFilter) {
          // Special handling for BluCar region
          if (regionFilter === "BluCar") {
            if (!yardInfo || !yardInfo.blucar) {
              return false;
            }
          } else {
            if (!yardInfo || yardInfo.region !== regionFilter) {
              return false;
            }
          }
        }
        
        return true;
      })
      .filter(item => {
        // Status filter - radio button style (single select)
        if (statusFilter.length === 0) return true;
        const offline = isDeviceOffline(item);
        
        // If only 'online' selected, show only online devices
        if (statusFilter.length === 1 && statusFilter.includes('online')) {
          return !offline;
        }
        // If only 'offline' selected, show only offline devices
        if (statusFilter.length === 1 && statusFilter.includes('offline')) {
          return offline;
        }
        return true;
      })
      .filter(item => {
        // Network type filter (WiFi / Cellular)
        if (!networkFilter) return true;
        return getNetworkType(item) === networkFilter;
      })
      .filter(item => {
        // Cellular sub-type filter (e.g. LTE, 4G, 5G)
        if (networkFilter !== 'Cellular' || cellularTypeFilter.length === 0) return true;
        const carrier = (item.carrier || '').trim();
        return cellularTypeFilter.includes(carrier);
      });

    // Apply sorting. Rank by the speed the row actually shows so the order matches the
    // displayed columns. Respect the Network filter: when WiFi/Cellular is selected, rank by
    // that connection's speed only — otherwise a WiFi-filtered device whose only test is
    // cellular would rank by its cellular value. With no network filter, use the higher of
    // the two. Devices with no speed shown always sort last, regardless of direction.
    if (sortBy) {
      const col = sortBy.startsWith('upload') ? 'upload_mbps' : 'download_mbps';
      const ascending = sortBy.endsWith('-low');
      const shownSpeed = (item) => {
        const sources = networkFilter === 'WiFi'
          ? [item.wifi_speed_test?.[col]]
          : networkFilter === 'Cellular'
            ? [item.cellular_speed_test?.[col]]
            : [item.wifi_speed_test?.[col], item.cellular_speed_test?.[col]];
        let best = null;
        for (const v of sources) {
          const n = typeof v === 'number' ? v : parseFloat(v);
          if (Number.isFinite(n) && (best === null || n > best)) best = n;
        }
        return best;
      };
      filtered = [...filtered].sort((a, b) => {
        const av = shownSpeed(a);
        const bv = shownSpeed(b);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;  // no speed shown -> bottom
        if (bv === null) return -1; // no speed shown -> bottom
        return ascending ? av - bv : bv - av;
      });
    }

    return filtered;
  }, [deviceData, searchQuery, searchScope, chargingFilter, divisionFilter, regionFilter, statusFilter, networkFilter, cellularTypeFilter, sortBy]);

  // Calculate summary statistics from all device data (before filtering)
  const summaryStats = useMemo(() => {
    let total = deviceData.length;
    let online = 0;
    let offline = 0;

    deviceData.forEach(item => {
      const isOffline = isDeviceOffline(item);
      if (isOffline) {
        offline++;
      } else {
        online++;
      }
    });

    return { total, online, offline };
  }, [deviceData]);

  const toggleRow = (androidId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(androidId)) {
      newExpanded.delete(androidId);
    } else {
      newExpanded.add(androidId);
    }
    setExpandedRows(newExpanded);
  };

  /** Display timestamp in CST, 12-hour AM/PM. Backend may send "M/D/YYYY, h:mm:ss AM/PM CT" or legacy "YYYY-MM-DD HH:mm:ss CT"; normalize to 12-hour. */
  const formatTimestampCST = (ts) => {
    if (ts == null || ts === '') return null;
    const s = String(ts).trim();
    try {
      let d;
      if (s.endsWith(' CT')) {
        const trimmed = s.replace(/\s*CT\s*$/, '').trim();
        if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}:\d{2}$/.test(trimmed)) {
          d = new Date(trimmed.replace(' ', 'T') + '-06:00');
        } else {
          d = new Date(trimmed);
        }
      } else {
        d = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s) ? new Date(s + 'Z') : new Date(s);
      }
      if (Number.isNaN(d.getTime())) return s;
      return d.toLocaleString('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' CT';
    } catch {
      return s;
    }
  };

  const availableCellularTypes = useMemo(() => {
    const types = new Set();
    deviceData.forEach(item => {
      if (getNetworkType(item) === 'Cellular') {
        const carrier = (item.carrier || '').trim();
        if (carrier && carrier !== 'Unknown' && carrier !== 'N/A') {
          types.add(carrier);
        }
      }
    });
    return [...types].sort();
  }, [deviceData]);

  const getNetworkSignal = (item) => {
    const networkType = getNetworkType(item);
    if (networkType === 'WiFi') {
      return item.wifi_strength || item.wifi;
    }
    return item.lte_rsrp_dbm;
  };

  const getConnectionStatus = (item) => {
    const networkStatus = item.data_network_state || item.network_status || '';
    if (networkStatus === 'CONNECTED' || networkStatus === 'Connected') {
      return 'Connected';
    }
    return networkStatus || 'Unknown';
  };

  const getNetworkTypeDetail = (item) => {
    const networkType = getNetworkType(item);
    if (networkType === 'WiFi') {
      // Always show SSID when WiFi is connected
      if (item.ssid && item.ssid !== 'Unknown' && item.ssid !== 'N/A' && item.ssid !== '') {
        return item.ssid;
      }
      return 'WiFi';
    } else if (networkType === 'Cellular') {
      // Always show carrier name when Cellular is connected
      const carrier = item.carrier ? item.carrier.trim() : '';
      const ratType = item.rat_type || item.network_type || '';
      if (carrier) {
        return ratType ? `${carrier} ${ratType}`.trim() : carrier;
      }
      return ratType || 'Cellular';
    }
    return 'Unknown';
  };

  return (
    <div className="w-full px-4 py-6">
      {portalEmail ? (
        <div className="mb-3 text-sm text-gray-700">
          Access scope: <span className="font-semibold">{portalScope || 'GLOBAL'}</span>
        </div>
      ) : null}
      {/* Filter bar: search + summary inline, collapsible */}
      <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
        {/* Main row: search, scope, collapse toggle, summary */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Unified search + scope */}
          <div className="flex flex-1 min-w-[200px] gap-2 items-center">
            <input
              type="text"
              placeholder={searchScope === 'yard' ? 'Yard number' : 'Search by ID, name, or yard'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 min-w-[140px] p-2 border border-gray-300 rounded text-sm"
            />
            <select
              value={searchScope}
              onChange={(e) => setSearchScope(e.target.value)}
              className="p-2 border border-gray-300 rounded text-sm bg-white"
              title="Search scope"
            >
              <option value="all">All</option>
              <option value="name_id">Name/ID</option>
              <option value="yard">Yard #</option>
            </select>
          </div>

          {/* Collapse/expand toggle */}
          <button
            type="button"
            onClick={() => setFilterBarCollapsed((c) => !c)}
            className="p-2 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-600"
            title={filterBarCollapsed ? 'Expand filters' : 'Collapse filters'}
          >
            {filterBarCollapsed ? <FaChevronDown /> : <FaChevronUp />}
          </button>

          {/* Summary inline */}
          <div className="flex items-center gap-1.5 text-sm shrink-0">
            <span className="font-semibold text-blue-600">{summaryStats.total}</span>
            <span className="text-gray-500">devices</span>
            <span className="text-gray-400">·</span>
            <span className="text-green-600 font-medium">{summaryStats.online}</span>
            <span className="text-gray-500">online</span>
            <span className="text-gray-400">·</span>
            <span className="text-red-600 font-medium">{summaryStats.offline}</span>
            <span className="text-gray-500">offline</span>
          </div>
        </div>

        {/* Expanded: Status, Filters button, active chips */}
        {!filterBarCollapsed && (
          <>
            <div className="flex flex-wrap gap-3 items-center mt-3 pt-3 border-t border-gray-200">
              {/* Status */}
              <div className="flex items-center gap-2 p-2 border border-gray-300 rounded bg-white">
                <span className="text-sm font-medium text-gray-700 mr-1">Status:</span>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="statusFilter" checked={statusFilter.length === 0 || (statusFilter.includes('online') && statusFilter.includes('offline'))} onChange={() => setStatusFilter([])} className="form-radio" />
                  <span className="text-sm">All</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="statusFilter" checked={statusFilter.length === 1 && statusFilter.includes('online') && !statusFilter.includes('offline')} onChange={() => setStatusFilter(['online'])} className="form-radio" />
                  <span className="text-sm">Online</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="statusFilter" checked={statusFilter.length === 1 && statusFilter.includes('offline') && !statusFilter.includes('online')} onChange={() => setStatusFilter(['offline'])} className="form-radio" />
                  <span className="text-sm">Offline</span>
                </label>
              </div>

              {/* Filters toggle */}
              {(() => {
                const activeCount = (chargingFilter.length ? 1 : 0) + (sortBy ? 1 : 0) + (divisionFilter ? 1 : 0) + (regionFilter ? 1 : 0) + (networkFilter ? 1 : 0);
                return (
                  <button
                    type="button"
                    onClick={() => setFiltersExpanded((e) => !e)}
                    className={`flex items-center gap-2 px-3 py-2 rounded border bg-white text-sm font-medium ${filtersExpanded ? 'border-blue-500 text-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <FaFilter />
                    Filters
                    {activeCount > 0 && <span className="bg-blue-100 text-blue-700 rounded-full px-1.5 text-xs">{activeCount}</span>}
                  </button>
                );
              })()}

              {/* Fleet AI Diagnostics button */}
              <button
                type="button"
                onClick={() => setFleetDiagnosticsOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded border border-violet-300 bg-violet-50 text-violet-700 text-sm font-medium hover:bg-violet-100 transition-colors"
                title="Run AI analysis across all fleet devices"
              >
                <FaRobot className="text-xs" />
                AI Diagnose
              </button>

              {/* Active filter chips */}
              <div className="flex flex-wrap gap-2 items-center">
                {statusFilter.length === 1 && statusFilter.includes('online') && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs">Online <button type="button" onClick={() => setStatusFilter([])} className="hover:opacity-70">×</button></span>
                )}
                {statusFilter.length === 1 && statusFilter.includes('offline') && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs">Offline <button type="button" onClick={() => setStatusFilter([])} className="hover:opacity-70">×</button></span>
                )}
                {chargingFilter.length === 1 && chargingFilter.includes('Charging') && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">Charging <button type="button" onClick={() => setChargingFilter([])} className="hover:opacity-70">×</button></span>
                )}
                {chargingFilter.length === 1 && chargingFilter.includes('Discharging') && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">Discharging <button type="button" onClick={() => setChargingFilter([])} className="hover:opacity-70">×</button></span>
                )}
                {sortBy && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs">
                    Speed: {sortBy === 'download-low' ? 'DL ↑' : sortBy === 'download-high' ? 'DL ↓' : sortBy === 'upload-low' ? 'UL ↑' : 'UL ↓'}
                    <button type="button" onClick={() => setSortBy('')} className="hover:opacity-70">×</button>
                  </span>
                )}
                {divisionFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs">{divisionFilter} <button type="button" onClick={() => setDivisionFilter('')} className="hover:opacity-70">×</button></span>
                )}
                {regionFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-xs">{regionFilter} <button type="button" onClick={() => setRegionFilter('')} className="hover:opacity-70">×</button></span>
                )}
                {networkFilter === 'WiFi' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-100 text-sky-800 text-xs">WiFi <button type="button" onClick={() => setNetworkFilter('')} className="hover:opacity-70">×</button></span>
                )}
                {networkFilter === 'Cellular' && cellularTypeFilter.length === 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-xs">Cellular <button type="button" onClick={() => { setNetworkFilter(''); setCellularTypeFilter([]); }} className="hover:opacity-70">×</button></span>
                )}
                {networkFilter === 'Cellular' && cellularTypeFilter.length > 0 && (
                  <>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-xs">
                      Cellular: {cellularTypeFilter.join(', ')}
                      <button type="button" onClick={() => { setNetworkFilter(''); setCellularTypeFilter([]); }} className="hover:opacity-70">×</button>
                    </span>
                  </>
                )}
                {searchScope === 'yard' && searchQuery.trim() && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-teal-100 text-teal-800 text-xs">Yard: {searchQuery.trim()} <button type="button" onClick={() => { setSearchScope('all'); setSearchQuery(''); }} className="hover:opacity-70">×</button></span>
                )}
              </div>
            </div>

            {/* Secondary filters (expandable) */}
            {filtersExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-4 items-end">
                <div className="flex items-center gap-2 p-2 border border-gray-300 rounded bg-white">
                  <span className="text-sm font-medium text-gray-700 mr-2">Charge State:</span>
                  <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="chargingFilter" checked={chargingFilter.length === 0} onChange={() => setChargingFilter([])} className="form-radio" /><span className="text-sm">All</span></label>
                  <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="chargingFilter" checked={chargingFilter.length === 1 && chargingFilter.includes('Charging')} onChange={() => setChargingFilter(['Charging'])} className="form-radio" /><span className="text-sm">Charging</span></label>
                  <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="chargingFilter" checked={chargingFilter.length === 1 && chargingFilter.includes('Discharging')} onChange={() => setChargingFilter(['Discharging'])} className="form-radio" /><span className="text-sm">Discharging</span></label>
                </div>
                <div className="flex flex-col gap-2 p-2 border border-gray-300 rounded bg-white">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 mr-2">Network:</span>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" name="networkFilter" checked={networkFilter === ''} onChange={() => { setNetworkFilter(''); setCellularTypeFilter([]); }} className="form-radio" />
                      <span className="text-sm">All</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" name="networkFilter" checked={networkFilter === 'WiFi'} onChange={() => { setNetworkFilter('WiFi'); setCellularTypeFilter([]); }} className="form-radio" />
                      <span className="text-sm">WiFi</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" name="networkFilter" checked={networkFilter === 'Cellular'} onChange={() => { setNetworkFilter('Cellular'); setCellularTypeFilter([]); }} className="form-radio" />
                      <span className="text-sm">Cellular</span>
                    </label>
                  </div>
                  {networkFilter === 'Cellular' && availableCellularTypes.length > 0 && (
                    <div className="flex flex-wrap gap-2 pl-1 pt-1 border-t border-gray-200 mt-1">
                      <span className="text-xs text-gray-500 self-center mr-1">Type:</span>
                      {availableCellularTypes.map(type => (
                        <label key={type} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={cellularTypeFilter.includes(type)}
                            onChange={() => {
                              setCellularTypeFilter(prev =>
                                prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
                              );
                            }}
                            className="form-checkbox"
                          />
                          <span className="text-sm">{type}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 p-2 border border-gray-300 rounded bg-white">
                  <span className="text-sm font-medium text-gray-700 mr-2">Speed:</span>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1">
                    <option value="">None</option>
                    <option value="download-low">Download (Low→High)</option>
                    <option value="download-high">Download (High→Low)</option>
                    <option value="upload-low">Upload (Low→High)</option>
                    <option value="upload-high">Upload (High→Low)</option>
                  </select>
                </div>
                <div className="w-40">
                  <Select
                    options={[{ value: "", label: "All Divisions" }, ...divisions.map(div => ({ value: div, label: div }))]}
                    value={divisionFilter ? { value: divisionFilter, label: divisionFilter } : { value: "", label: "All Divisions" }}
                    onChange={(selectedOption) => setDivisionFilter(selectedOption?.value || "")}
                    placeholder="Division..."
                    isClearable
                    isSearchable
                    className="w-full"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), menu: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
                <div className="w-40">
                  <Select
                    options={[{ value: "", label: "All Regions" }, ...regions.map(reg => ({ value: reg, label: reg }))]}
                    value={regionFilter ? { value: regionFilter, label: regionFilter } : { value: "", label: "All Regions" }}
                    onChange={(selectedOption) => setRegionFilter(selectedOption?.value || "")}
                    placeholder="Region..."
                    isClearable
                    isSearchable
                    isDisabled={!divisionFilter}
                    className="w-full"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), menu: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {fetchError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {fetchError}
        </div>
      )}

      {/* Compact Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-white shadow-lg rounded-lg">
          <thead className="bg-blue-600 text-white">
            <tr>
              <th className="p-3 text-left">Device</th>
              <th className="p-3 text-left">Yard</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Battery</th>
              <th className="p-3 text-left">Network</th>
              <th className="p-3 text-center w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-500">
                  Loading devices...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-500">
                  {searchQuery || chargingFilter.length > 0 || divisionFilter || regionFilter || (searchScope === 'yard' && searchQuery) || statusFilter.length > 0 || networkFilter
                    ? "No devices found matching your filters"
                    : "No devices found"}
                </td>
              </tr>
            ) : (
              filteredData.map((item) => {
                // Calculate time difference first (same as original component)
                let diffMins = 0;
                if (item.timestamp) {
                  try {
                    const now = new Date();
                    const lastSeen = new Date(item.timestamp);
                    if (!isNaN(lastSeen.getTime())) {
                      const diffMs = now.getTime() - lastSeen.getTime();
                      if (diffMs > 0) {
                        diffMins = diffMs / 60000;
                      }
                    }
                  } catch (e) {
                    diffMins = Infinity;
                  }
                } else {
                  diffMins = Infinity;
                }

                // Determine offline status based on diffMins
                const offline = diffMins === Infinity || diffMins > 30;
                
                // Calculate time display
                let timeAgo = "N/A";
                if (diffMins !== Infinity && !isNaN(diffMins)) {
                  if (diffMins < 1) timeAgo = "1 min ago";
                  else if (diffMins < 60) timeAgo = `${Math.floor(diffMins)} min ago`;
                  else {
                    const diffHours = Math.floor(diffMins / 60);
                    if (diffHours < 24) timeAgo = `${diffHours}h ago`;
                    else {
                      const diffDays = Math.floor(diffHours / 24);
                      timeAgo = `${diffDays}d ago`;
                    }
                  }
                }

                const batteryPercent = item.battery_percent || 0;
                const isCharging = (item.charging_state || item.ac || '').toString().toLowerCase() === 'charging' || 
                                  (item.charging_state || item.ac || '').toString().toLowerCase() === 'ac' ||
                                  (item.charging_state || item.ac || '').toString().toLowerCase() === 'true';
                const networkType = getNetworkType(item);
                const networkSignal = getNetworkSignal(item);
                const signalInfo = getSignalStrength(networkSignal);
                const isExpanded = expandedRows.has(item.android_id);

                const yard = yardsData.find(y => String(y.Yard) === String(item.yard_number));
                const yardName = yard ? `${yard.City}, ${yard.State}` : "Unknown";

                return (
                  <React.Fragment key={item.android_id}>
                    <tr 
                      className={`border-b hover:bg-gray-50 transition-colors ${
                        offline ? 'bg-red-50' : 'bg-white'
                      }`}
                    >
                      {/* Device */}
                      <td className="p-3">
                        <div 
                          className="font-semibold"
                        >
                          {item.device_friendly_name || item.android_id}
                        </div>
                        <div className="text-sm text-gray-600">{getFriendlyName(item.device_name)}</div>
                        <div className="text-xs text-gray-400 mt-1 break-all">Android ID: {item.android_id}</div>
                        {item.asset_tag && (
                          <div className="text-xs text-gray-400 mt-1 break-all">UUID: {item.asset_tag}</div>
                        )}
                        {item.device_friendly_name && (
                          <button
                            onClick={() => {
                              const uemUrl = `https://cn927.awmdm.com/AirWatch/#/AirWatch/Search?query=${encodeURIComponent(item.device_friendly_name)}`;
                              window.open(uemUrl, '_blank', 'noopener,noreferrer');
                            }}
                            className="text-[10px] px-1.5 py-0.5 mt-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            title="Open device in UEM"
                          >
                            More Info
                          </button>
                        )}
                      </td>

                      {/* Yard */}
                      <td className="p-3">
                        {item.yard_number ? (
                          <div>
                            <div className="font-semibold">Yard {item.yard_number}</div>
                            <div className="text-sm text-gray-600">{yardName}</div>
                            {uemDataByDeviceId[item.android_id]?.uem_gps_latitude && uemDataByDeviceId[item.android_id]?.uem_gps_longitude && (
                              <div className="text-xs text-orange-600 font-semibold mt-1">
                                📍 UEM: {uemDataByDeviceId[item.android_id].uem_gps_latitude.toFixed(6)}, {uemDataByDeviceId[item.android_id].uem_gps_longitude.toFixed(6)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              offline ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {offline ? 'Offline' : 'Online'}
                            </span>
                            <span className="text-xs text-gray-500">{timeAgo}</span>
                          </div>
                          {(() => {
                            const speedTestData = lastSpeedTestTimestamps[item.android_id];
                            
                            // Show last speed test if available
                            let lastTestDisplay = null;
                            if (speedTestData && speedTestData.last_speed_test_timestamp) {
                              try {
                                // Parse timestamp - backend sends UTC timestamp in "YYYY-MM-DD HH:mm:ss" format
                                // Need to explicitly parse as UTC to avoid timezone issues
                                let speedTestDate;
                                const timestampStr = speedTestData.last_speed_test_timestamp;
                                
                                // Backend may send "YYYY-MM-DD HH:mm:ss CT" (CST) or legacy UTC format
                                const trimmed = String(timestampStr).replace(/\s*CT\s*$/, '').trim();
                                if (typeof timestampStr === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
                                  if (timestampStr.endsWith(' CT')) {
                                    speedTestDate = new Date(trimmed.replace(' ', 'T') + '-06:00');
                                  } else {
                                    speedTestDate = new Date(trimmed.replace(' ', 'T') + 'Z');
                                  }
                                } else {
                                  speedTestDate = new Date(trimmed);
                                }
                                
                                // Check if date is valid
                                if (isNaN(speedTestDate.getTime())) {
                                  console.warn(`Invalid speed test timestamp for device ${item.android_id}: ${speedTestData.last_speed_test_timestamp}`);
                                  lastTestDisplay = (
                                    <div className="text-xs text-gray-400">
                                      Last speed test: Never
                                    </div>
                                  );
                                } else {
                                  const now = new Date();
                                  const diffMs = now.getTime() - speedTestDate.getTime();
                                  
                                  // Check if timestamp is in the future (invalid) - allow small margin for clock skew
                                  if (diffMs < -60000) { // More than 1 minute in the future (account for clock skew)
                                    console.warn(`Speed test timestamp is in the future for device ${item.android_id}: ${speedTestData.last_speed_test_timestamp} (parsed as: ${speedTestDate.toISOString()}, diff: ${diffMs}ms)`);
                                    lastTestDisplay = (
                                      <div className="text-xs text-gray-400">
                                        Last speed test: Never
                                      </div>
                                    );
                                  } else {
                                    const diffMins = diffMs / 60000;
                                    
                                    // Only show "1 min ago" if it's actually between 1-2 minutes
                                    let speedTestAgo = "";
                                    if (diffMins < 1) {
                                      speedTestAgo = "Just now";
                                    } else if (diffMins < 2) {
                                      speedTestAgo = "1 min ago";
                                    } else if (diffMins < 60) {
                                      speedTestAgo = `${Math.floor(diffMins)} min ago`;
                                    } else {
                                      const diffHours = Math.floor(diffMins / 60);
                                      if (diffHours < 24) speedTestAgo = `${diffHours}h ago`;
                                      else {
                                        const diffDays = Math.floor(diffHours / 24);
                                        speedTestAgo = `${diffDays}d ago`;
                                      }
                                    }
                                    
                                    lastTestDisplay = (
                                      <div className="text-xs text-gray-500">
                                        Last speed test: {speedTestAgo}
                                      </div>
                                    );
                                  }
                                }
                              } catch (e) {
                                console.error('Error parsing speed test timestamp:', e);
                                lastTestDisplay = (
                                  <div className="text-xs text-gray-400">
                                    Last speed test: Never
                                  </div>
                                );
                              }
                            } else {
                              lastTestDisplay = (
                                <div className="text-xs text-gray-400">
                                  Last speed test: Never
                                </div>
                              );
                            }
                            
                            // Always show next scheduled test if available
                            return (
                              <div className="flex flex-col gap-1">
                                {lastTestDisplay}
                                {speedTestData && speedTestData.next_speed_test_time && (
                                  <div className="text-xs text-blue-500 font-medium">
                                    Next: {formatTimestampCST(speedTestData.next_speed_test_time)}
                                  </div>
                                )}
                                {speedTestData && speedTestData.missed_test && (
                                  <div className="text-xs text-red-600 font-semibold flex items-center gap-1">
                                    <span>⚠️</span>
                                    <span>Missed scheduled test</span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </td>

                      {/* Battery */}
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getBatteryIcon(batteryPercent, isCharging)}
                          <div className="flex-1">
                            <div className="font-semibold">{batteryPercent}%</div>
                            <div className="text-xs text-gray-500">
                              {isCharging ? 'Charging' : 'Discharging'}
                            </div>
                            {item.battery_temperature != null && (
                              <div className={`text-xs font-medium ${getBatteryTempColor(celsiusToFahrenheit(item.battery_temperature))}`}>
                                Temp: {celsiusToFahrenheit(item.battery_temperature)}°F
                              </div>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setGraphModal({
                                isOpen: true,
                                androidId: item.android_id,
                                deviceName: item.device_friendly_name || item.android_id,
                                graphType: 'battery'
                              });
                            }}
                            className="text-[8px] px-1 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            title="View 24h battery graph"
                          >
                            <FaChartLine />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setGraphModal({
                                isOpen: true,
                                androidId: item.android_id,
                                deviceName: item.device_friendly_name || item.android_id,
                                graphType: 'temperature'
                              });
                            }}
                            className="text-[8px] px-1 py-0.5 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                            title="View temperature graph"
                          >
                            <FaThermometerHalf />
                          </button>
                        </div>
                      </td>

                      {/* Network */}
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {networkType === 'WiFi' ? (
                            <FaWifi className={signalInfo.colorClass} />
                          ) : (
                            <FaSignal className={signalInfo.colorClass} />
                          )}
                          <div className="flex-1">
                            {/* Network Info - Raw Telemetry */}
                            <div className="text-sm">
                              <span className="font-semibold">{networkType}</span>
                              {(() => {
                                const networkDetail = getNetworkTypeDetail(item);
                                return (
                                  <>
                                    {' • '}
                                    <span>{networkDetail}</span>
                                  </>
                                );
                              })()}
                              <span className="text-xs text-gray-500 ml-1">(currently device connected)</span>
                            </div>
                            {networkSignal != null && (
                              <div className="text-xs text-gray-500">
                                {networkSignal} dBm
                              </div>
                            )}
                            
                            {/* Visual Separator */}
                            <div className="border-t border-gray-200 my-2"></div>
                            
                            {/* Speed Test Results - Two Columns: WiFi | Cellular */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              {/* WiFi Column */}
                              <div className="flex flex-col border-r border-gray-200 pr-2">
                                <div className="font-semibold text-xs text-gray-600 mb-1">WiFi</div>
                                {item.wifi_speed_test ? (
                                  <>
                                    <div>
                                      <span className={`${getDownloadColor(item.wifi_speed_test.download_mbps || 0)} font-semibold`}>
                                        ⬇ {item.wifi_speed_test.download_mbps?.toFixed(1) || '0.0'} Mbps
                                      </span>
                                      {' / '}
                                      <span className={getUploadColor(item.wifi_speed_test.upload_mbps || 0)}>
                                        ⬆ {item.wifi_speed_test.upload_mbps?.toFixed(1) || '0.0'} Mbps
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {formatTimestampCST(item.wifi_speed_test.timestamp) ?? 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      WiFi: {item.ssid && item.ssid !== 'Unknown' ? item.ssid : 'N/A'}
                                    </div>
                                    {item.wifi_speed_test?.location_latitude != null && item.wifi_speed_test?.location_longitude != null && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSpeedTestMapModal({
                                            open: true,
                                            lat: Number(item.wifi_speed_test.location_latitude),
                                            lng: Number(item.wifi_speed_test.location_longitude),
                                            uploadMbps: item.wifi_speed_test.upload_mbps,
                                            downloadMbps: item.wifi_speed_test.download_mbps,
                                            signalDbm: item.wifi_speed_test.signal_rsrp_dbm,
                                            label: 'WiFi',
                                          });
                                        }}
                                        className="mt-1 flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800"
                                        title="Show on map"
                                      >
                                        <FaMapMarkerAlt /> Show on map
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-xs text-gray-400">N/A</div>
                                )}
                              </div>
                              
                              {/* Cellular Column */}
                              <div className="flex flex-col pl-2">
                                <div className="font-semibold text-xs text-gray-600 mb-1">Cellular</div>
                                {item.cellular_speed_test ? (
                                  <>
                                    <div>
                                      <span className={`${getDownloadColor(item.cellular_speed_test.download_mbps || 0)} font-semibold`}>
                                        ⬇ {item.cellular_speed_test.download_mbps?.toFixed(1) || '0.0'} Mbps
                                      </span>
                                      {' / '}
                                      <span className={getUploadColor(item.cellular_speed_test.upload_mbps || 0)}>
                                        ⬆ {item.cellular_speed_test.upload_mbps?.toFixed(1) || '0.0'} Mbps
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {formatTimestampCST(item.cellular_speed_test.timestamp) ?? 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      Mobile Data: {item.cellular_speed_test.carrier_name || (networkType === 'Cellular' ? getNetworkTypeDetail(item) : null) || item.carrier || 'N/A'}
                                    </div>
                                    {item.cellular_speed_test?.location_latitude != null && item.cellular_speed_test?.location_longitude != null && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSpeedTestMapModal({
                                            open: true,
                                            lat: Number(item.cellular_speed_test.location_latitude),
                                            lng: Number(item.cellular_speed_test.location_longitude),
                                            uploadMbps: item.cellular_speed_test.upload_mbps,
                                            downloadMbps: item.cellular_speed_test.download_mbps,
                                            signalDbm: item.cellular_speed_test.signal_rsrp_dbm,
                                            label: 'Cellular',
                                          });
                                        }}
                                        className="mt-1 flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800"
                                        title="Show on map"
                                      >
                                        <FaMapMarkerAlt /> Show on map
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-xs text-gray-400">N/A</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const params = new URLSearchParams();
                                params.set('androidId', item.android_id);
                                params.set('deviceName', item.device_friendly_name || item.android_id);
                                if (item.yard_number != null && String(item.yard_number).trim() !== '') {
                                  params.set('yard', String(item.yard_number));
                                }
                                if (portalEmail) {
                                  params.set('portal_email', portalEmail);
                                  params.set('portal_scope', portalScope);
                                }
                                navigate(`/HeatMap?${params.toString()}`);
                              }}
                              className="text-[8px] px-1 py-0.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                              title="Speed test history on map"
                            >
                              <FaMapMarkerAlt />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setGraphModal({
                                  isOpen: true,
                                  androidId: item.android_id,
                                  deviceName: item.device_friendly_name || item.android_id,
                                  graphType: 'speed'
                                });
                              }}
                              className="text-[8px] px-1 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              title="View 24h speed graph"
                            >
                              <FaChartLine />
                            </button>
                            {manualSpeedTestEnabled && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  triggerSpeedTest(item.android_id);
                                }}
                                disabled={speedTestStatus[item.android_id]?.loading}
                                className={`text-[8px] px-1 py-0.5 rounded transition-colors ${
                                  speedTestStatus[item.android_id]?.loading
                                    ? 'bg-gray-400 cursor-not-allowed text-white'
                                    : 'bg-green-500 hover:bg-green-600 text-white'
                                }`}
                                title="Run speed test"
                              >
                                {speedTestStatus[item.android_id]?.loading ? '⏳' : '⚡'}
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAppSpeedTestButton(item.android_id);
                              }}
                              disabled={appButtonLoadingByDevice[item.android_id]}
                              className={`text-[8px] px-1 py-0.5 rounded transition-colors ${
                                appButtonLoadingByDevice[item.android_id]
                                  ? 'bg-gray-400 cursor-not-allowed text-white'
                                  : appButtonEnabledByDevice[item.android_id] !== false
                                    ? 'bg-blue-500 hover:bg-blue-600 text-white'
                                    : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                              }`}
                              title={appButtonEnabledByDevice[item.android_id] !== false 
                                ? "App speed test button is ENABLED - click to disable" 
                                : "App speed test button is DISABLED - click to enable"}
                            >
                              {appButtonLoadingByDevice[item.android_id] ? '⏳' : '📱'}
                            </button>
                          </div>
                        </div>
                      </td>

                      {/* Expand Button */}
                      <td className="p-3 text-center">
                        <button
                          onClick={() => toggleRow(item.android_id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                          title={isExpanded ? "Collapse details" : "Expand details"}
                        >
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="6" className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Device Identity */}
                            <div className="bg-white p-4 rounded shadow">
                              <div className="flex justify-between items-center mb-2">
                                <h3 className="font-semibold text-blue-600">Device Identity</h3>
                                <div className="flex items-center gap-2">
                                  {uemDataByDeviceId[item.android_id] && (
                                    <span className="text-xs text-orange-600 font-semibold" title="Values in orange are from UEM API">UEM</span>
                                  )}
                                  <button
                                    onClick={() => fetchUemData(item.android_id)}
                                    disabled={uemLoadingByDeviceId[item.android_id]}
                                    className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                    title="Fetch latest UEM information"
                                  >
                                    {uemLoadingByDeviceId[item.android_id] ? 'Loading...' : 'Update Info'}
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-1 text-sm">
                                <div><span className="font-medium">Android ID:</span> {item.android_id || "-"}</div>
                                {item.asset_tag && (
                                  <div><span className="font-medium">UUID:</span> <span className="break-all">{item.asset_tag}</span></div>
                                )}
                                {uemDataByDeviceId[item.android_id]?.serial_number && (
                                  <div><span className="font-medium">Serial Number:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].serial_number}</span></div>
                                )}
                                {uemDataByDeviceId[item.android_id]?.imei && (
                                  <div><span className="font-medium">IMEI:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].imei}</span></div>
                                )}
                                <div><span className="font-medium">Device Friendly Name:</span> {item.device_friendly_name || "-"}</div>
                                <div><span className="font-medium">Model:</span> {getFriendlyName(item.device_name)}</div>
                                <div><span className="font-medium">Last Data:</span> {timeAgo}</div>
                              </div>
                            </div>

                            {/* Location */}
                            <div className="bg-white p-4 rounded shadow">
                              <h3 className="font-semibold mb-2 text-blue-600">Location</h3>
                              <div className="space-y-1 text-sm">
                                <div><span className="font-medium">Yard:</span> {item.yard_number ? `Yard ${item.yard_number}` : "-"}</div>
                                <div><span className="font-medium">Location:</span> {yardName}</div>
                                {item.address && (
                                  <div className="text-xs text-gray-600 break-words">{item.address}</div>
                                )}
                                {item.gps && (
                                  <div className="text-xs text-gray-500">GPS: {item.gps}</div>
                                )}
                                {uemDataByDeviceId[item.android_id]?.uem_gps_latitude && uemDataByDeviceId[item.android_id]?.uem_gps_longitude && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <div className="text-xs font-semibold text-orange-600 mb-1">📍 UEM GPS Location</div>
                                    <div className="text-xs text-orange-600">
                                      Latitude: {uemDataByDeviceId[item.android_id].uem_gps_latitude.toFixed(6)}
                                    </div>
                                    <div className="text-xs text-orange-600">
                                      Longitude: {uemDataByDeviceId[item.android_id].uem_gps_longitude.toFixed(6)}
                                    </div>
                                    {uemDataByDeviceId[item.android_id].uem_gps_elevation && (
                                      <div className="text-xs text-orange-600">
                                        Elevation: {uemDataByDeviceId[item.android_id].uem_gps_elevation.toFixed(2)} m
                                      </div>
                                    )}
                                    {uemDataByDeviceId[item.android_id].uem_gps_sample_time && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Sample Time: {new Date(uemDataByDeviceId[item.android_id].uem_gps_sample_time).toLocaleString('en-US', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                          hour12: true,
                                          timeZone: 'America/Chicago'
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Network Overview */}
                            <div className="bg-white p-4 rounded shadow">
                              <h3 className="font-semibold mb-2 text-blue-600">Network Overview</h3>
                              <div className="space-y-1 text-sm">
                                <div><span className="font-medium">Type:</span> {networkType}</div>
                                <div><span className="font-medium">SSID:</span> {
                                  uemDataByDeviceId[item.android_id]?.wifi_ssid ? (
                                    <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].wifi_ssid}</span>
                                  ) : (
                                    item.ssid || item.wifi || "-"
                                  )
                                }</div>
                                {uemDataByDeviceId[item.android_id]?.home_carrier && (
                                  <div><span className="font-medium">Home Carrier:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].home_carrier}</span></div>
                                )}
                                {uemDataByDeviceId[item.android_id]?.current_carrier && (
                                  <div><span className="font-medium">Current Carrier:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].current_carrier}</span></div>
                                )}
                                {!uemDataByDeviceId[item.android_id]?.current_carrier && (
                                  <div><span className="font-medium">Carrier:</span> {item.carrier || "-"}</div>
                                )}
                                <div><span className="font-medium">IP:</span> {
                                  uemDataByDeviceId[item.android_id]?.wifi_ip ? (
                                    <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].wifi_ip}</span>
                                  ) : (
                                    item.ip_address || "-"
                                  )
                                }</div>
                                {uemDataByDeviceId[item.android_id]?.wifi_mac && (
                                  <div><span className="font-medium">Wi-Fi MAC:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].wifi_mac}</span></div>
                                )}
                              </div>
                            </div>

                            {/* Signal Quality */}
                            <div className="bg-white p-4 rounded shadow">
                              <h3 className="font-semibold mb-2 text-blue-600">Signal Quality</h3>
                              <div className="space-y-1 text-sm">
                                {networkType === 'WiFi' ? (
                                  <>
                                    <div><span className="font-medium">WiFi Signal:</span> {item.wifi_strength || item.wifi || "-"} dBm</div>
                                    <div><span className="font-medium">RSSI:</span> {item.wifi_strength || "-"}</div>
                                  </>
                                ) : (
                                  <>
                                    <div><span className="font-medium">Cell Signal:</span> {item.lte_rsrp_dbm || "-"} dBm</div>
                                    <div><span className="font-medium">RSRQ:</span> {item.lte_rsrq_db || "-"} dB</div>
                                    <div><span className="font-medium">RSSNR:</span> {item.lte_rssnr_db || "-"} dB</div>
                                    <div><span className="font-medium">CQI:</span> {item.lte_cqi || "-"}</div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Speed Test - Two Side-by-Side Boxes */}
                            <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Speed Test Results - Side by Side */}
                              <div className="grid grid-cols-2 gap-4">
                                {/* WiFi Speed Test Box */}
                                <div className="bg-white p-4 rounded shadow">
                                  <h3 className="font-semibold mb-2 text-blue-600">WiFi Speed Test</h3>
                                  {item.wifi_speed_test ? (
                                    <div className="space-y-1 text-sm">
                                      <div><span className="font-medium">Download:</span> {item.wifi_speed_test.download_mbps != null ? `${item.wifi_speed_test.download_mbps.toFixed(2)} Mbps` : "-"}</div>
                                      <div><span className="font-medium">Upload:</span> {item.wifi_speed_test.upload_mbps != null ? `${item.wifi_speed_test.upload_mbps.toFixed(2)} Mbps` : "-"}</div>
                                      <div><span className="font-medium">Ping:</span> {item.wifi_speed_test.ping_ms != null ? `${item.wifi_speed_test.ping_ms.toFixed(1)} ms` : "-"}</div>
                                      <div><span className="font-medium">Jitter:</span> {item.wifi_speed_test.jitter_ms != null ? `${item.wifi_speed_test.jitter_ms} ms` : "-"}</div>
                                      <div><span className="font-medium">Packet Loss:</span> {item.wifi_speed_test.packet_loss_percent != null ? `${item.wifi_speed_test.packet_loss_percent}%` : "-"}</div>
                                      <div><span className="font-medium">Network:</span> {item.ssid && item.ssid !== 'Unknown' ? item.ssid : 'WiFi'}</div>
                                      <div><span className="font-medium">Tested:</span> {formatTimestampCST(item.wifi_speed_test.timestamp) ?? "-"}</div>
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-400">N/A - No WiFi speed test available</div>
                                  )}
                                </div>

                                {/* Cellular Speed Test Box */}
                                <div className="bg-white p-4 rounded shadow">
                                  <h3 className="font-semibold mb-2 text-blue-600">Cellular Speed Test</h3>
                                  {item.cellular_speed_test ? (
                                    <div className="space-y-1 text-sm">
                                      <div><span className="font-medium">Download:</span> {item.cellular_speed_test.download_mbps != null ? `${item.cellular_speed_test.download_mbps.toFixed(2)} Mbps` : "-"}</div>
                                      <div><span className="font-medium">Upload:</span> {item.cellular_speed_test.upload_mbps != null ? `${item.cellular_speed_test.upload_mbps.toFixed(2)} Mbps` : "-"}</div>
                                      <div><span className="font-medium">Ping:</span> {item.cellular_speed_test.ping_ms != null ? `${item.cellular_speed_test.ping_ms.toFixed(1)} ms` : "-"}</div>
                                      <div><span className="font-medium">Jitter:</span> {item.cellular_speed_test.jitter_ms != null ? `${item.cellular_speed_test.jitter_ms} ms` : "-"}</div>
                                      <div><span className="font-medium">Packet Loss:</span> {item.cellular_speed_test.packet_loss_percent != null ? `${item.cellular_speed_test.packet_loss_percent}%` : "-"}</div>
                                      <div><span className="font-medium">Carrier:</span> {item.cellular_speed_test.carrier_name || item.carrier || "-"}</div>
                                      <div><span className="font-medium">Tested:</span> {formatTimestampCST(item.cellular_speed_test.timestamp) ?? "-"}</div>
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-400">N/A - No Cellular speed test available</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Battery */}
                            <div className="bg-white p-4 rounded shadow">
                              <h3 className="font-semibold mb-2 text-blue-600">Battery</h3>
                              <div className="space-y-1 text-sm">
                                <div><span className="font-medium">Level:</span> {batteryPercent}%</div>
                                <div><span className="font-medium">Status:</span> {isCharging ? 'Charging' : (item.charging_state || item.ac || "-")}</div>
                                <div>
                                  <span className="font-medium">Temp:</span>{' '}
                                  {item.battery_temperature != null ? (
                                    <span className={celsiusToFahrenheit(item.battery_temperature) > 100 ? 'text-red-600 font-semibold' : ''}>
                                      {celsiusToFahrenheit(item.battery_temperature)} °F
                                    </span>
                                  ) : "-"}
                                </div>
                                <div><span className="font-medium">Health:</span> {item.battery_health || "-"}</div>
                                <div><span className="font-medium">Current:</span> {item.battery_current_ma != null ? `${item.battery_current_ma} mA` : "-"}</div>
                                <div><span className="font-medium">Capacity:</span> {item.battery_mAmp != null ? `${item.battery_mAmp} mAh` : "-"}</div>
                              </div>
                            </div>

                            {/* System Performance */}
                            <div className="bg-white p-4 rounded shadow">
                              <h3 className="font-semibold mb-2 text-blue-600">System Performance</h3>
                              <div className="space-y-1 text-sm">
                                <div><span className="font-medium">System Uptime:</span> {item.system_uptime_minutes != null ? `${Math.round(item.system_uptime_minutes)} min` : "-"}</div>
                                <div><span className="font-medium">App Uptime:</span> {item.uptime_minutes != null ? `${Math.round(item.uptime_minutes)} min` : "-"}</div>
                                <div><span className="font-medium">Wake Locks:</span> {item.active_wake_locks != null ? item.active_wake_locks : "-"}</div>
                                <div><span className="font-medium">CPU Usage:</span> {item.cpu_usage_percent != null ? `${item.cpu_usage_percent}%` : "-"}</div>
                                <div><span className="font-medium">CPU Cores:</span> {item.cpu_core_count || "-"}</div>
                                <div><span className="font-medium">Thermal Status:</span> {item.thermal_status || "-"}</div>
                                <div><span className="font-medium">Process Count:</span> {item.running_process_count || "-"}</div>
                              </div>
                            </div>

                            {/* Memory / Storage */}
                            <div className="bg-white p-4 rounded shadow">
                              <h3 className="font-semibold mb-2 text-blue-600">Memory / Storage</h3>
                              <div className="space-y-1 text-sm">
                                <div><span className="font-medium">Available RAM:</span> {item.available_mem_mb != null ? `${item.available_mem_mb} MB` : "-"}</div>
                                <div><span className="font-medium">Total RAM:</span> {item.total_mem_mb != null ? `${item.total_mem_mb} MB` : "-"}</div>
                                <div><span className="font-medium">App Memory:</span> {item.app_mem_usage_mb != null ? `${item.app_mem_usage_mb} MB` : "-"}</div>
                                <div><span className="font-medium">Memory Pressure:</span> {item.memory_pressure || "-"}</div>
                                <div><span className="font-medium">Internal Storage:</span> {item.internal_available_mb != null ? `${item.internal_available_mb} MB` : "-"}</div>
                              </div>
                            </div>

                            {/* Management (UEM Data) */}
                            {(uemDataByDeviceId[item.android_id]?.organization_group || 
                              uemDataByDeviceId[item.android_id]?.enrollment_status || 
                              uemDataByDeviceId[item.android_id]?.compliance_status) && (
                              <div className="bg-white p-4 rounded shadow">
                                <h3 className="font-semibold mb-2 text-blue-600">Management</h3>
                                <div className="space-y-1 text-sm">
                                  {uemDataByDeviceId[item.android_id]?.organization_group && (
                                    <div><span className="font-medium">Organization Group:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].organization_group}</span></div>
                                  )}
                                  {uemDataByDeviceId[item.android_id]?.enrollment_status && (
                                    <div><span className="font-medium">Enrollment Status:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].enrollment_status}</span></div>
                                  )}
                                  {uemDataByDeviceId[item.android_id]?.compliance_status && (
                                    <div><span className="font-medium">Compliance Status:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].compliance_status}</span></div>
                                  )}
                                  {uemDataByDeviceId[item.android_id]?.last_enrolled_on && (
                                    <div><span className="font-medium">Last Enrolled On:</span> <span className="text-orange-600 font-semibold">{new Date(uemDataByDeviceId[item.android_id].last_enrolled_on).toLocaleString()}</span></div>
                                  )}
                                  {uemDataByDeviceId[item.android_id]?.tags && (
                                    <div><span className="font-medium">Tags:</span> <span className="text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].tags}</span></div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Additional Information (UEM Data) */}
                            {uemDataByDeviceId[item.android_id]?.device_reported_name && (
                              <div className="bg-white p-4 rounded shadow">
                                <h3 className="font-semibold mb-2 text-blue-600">Additional Information</h3>
                                <div className="space-y-1 text-sm">
                                  <div><span className="font-medium">Device Reported Name:</span> <span className="break-all text-orange-600 font-semibold">{uemDataByDeviceId[item.android_id].device_reported_name}</span></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* UEM Device Modal */}
      {selectedDevice && (
        <UemDeviceModal
          uuid={selectedDevice.uuid}
          deviceFriendlyName={selectedDevice.deviceFriendlyName}
          onClose={() => setSelectedDevice(null)}
        />
      )}

      {/* Graph Modal */}
      <DeviceGraphModal
        isOpen={graphModal.isOpen}
        onClose={() => setGraphModal({ isOpen: false, androidId: null, deviceName: null, graphType: null })}
        androidId={graphModal.androidId}
        deviceName={graphModal.deviceName}
        graphType={graphModal.graphType}
      />

      {/* AI Diagnostics Modal */}
      <DiagnosticsModal
        isOpen={diagnosticsModal.isOpen}
        onClose={() => setDiagnosticsModal({ isOpen: false, androidId: null, deviceName: null })}
        androidId={diagnosticsModal.androidId}
        deviceName={diagnosticsModal.deviceName}
      />

      {/* Fleet AI Diagnostics Modal */}
      <FleetDiagnosticsModal
        isOpen={fleetDiagnosticsOpen}
        onClose={() => setFleetDiagnosticsOpen(false)}
      />

      {/* Speed Test Location Map Modal */}
      {speedTestMapModal.open && speedTestMapModal.lat != null && speedTestMapModal.lng != null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSpeedTestMapModal({ open: false, lat: null, lng: null, uploadMbps: null, downloadMbps: null, signalDbm: null, label: '' })}
        >
          <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col" style={{ width: '90vw', maxWidth: 560, height: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-gray-50">
              <span className="font-semibold text-gray-800">Speed test location – {speedTestMapModal.label}</span>
              <button
                type="button"
                onClick={() => setSpeedTestMapModal({ open: false, lat: null, lng: null, uploadMbps: null, downloadMbps: null, signalDbm: null, label: '' })}
                className="p-1 text-gray-500 hover:text-gray-700 rounded"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="flex-1 min-h-0 relative">
              <MapContainer
                center={[speedTestMapModal.lat, speedTestMapModal.lng]}
                zoom={17}
                scrollWheelZoom
                className="w-full h-full rounded-b-lg"
                style={{ minHeight: 340 }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker
                  position={[speedTestMapModal.lat, speedTestMapModal.lng]}
                  icon={L.divIcon({
                    className: 'speed-test-map-marker',
                    html: '<div style="width:24px;height:24px;background:#2563eb;border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>',
                    iconSize: [24, 24],
                    iconAnchor: [12, 12],
                  })}
                >
                  <Popup>
                    <div className="text-sm">
                      <div><strong>Upload:</strong> {speedTestMapModal.uploadMbps != null ? `${Number(speedTestMapModal.uploadMbps).toFixed(1)} Mbps` : 'N/A'}</div>
                      <div><strong>Download:</strong> {speedTestMapModal.downloadMbps != null ? `${Number(speedTestMapModal.downloadMbps).toFixed(1)} Mbps` : 'N/A'}</div>
                      <div><strong>Signal:</strong> {speedTestMapModal.signalDbm != null ? `${speedTestMapModal.signalDbm} dBm` : 'N/A'}</div>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </div>
      )}

      {/* Speed Test History Map Modal */}
      {speedTestHistoryModal.open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSpeedTestHistoryModal({ open: false, androidId: null, deviceName: null, results: [], loading: false })}
        >
          <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col" style={{ width: '90vw', maxWidth: 640, height: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-gray-50 flex-wrap gap-1">
              <span className="font-semibold text-gray-800">
                Speed test history – {speedTestHistoryModal.deviceName || speedTestHistoryModal.androidId || 'Device'}
              </span>
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 align-middle mr-1" />Latest</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 align-middle mr-1" />WiFi</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 align-middle mr-1" />Cellular</span>
              </div>
              <button
                type="button"
                onClick={() => setSpeedTestHistoryModal({ open: false, androidId: null, deviceName: null, results: [], loading: false })}
                className="p-1 text-gray-500 hover:text-gray-700 rounded"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="flex-1 min-h-0 relative">
              {speedTestHistoryModal.loading ? (
                <div className="flex items-center justify-center h-full min-h-[340px]">
                  <span className="text-gray-500">Loading history...</span>
                </div>
              ) : speedTestHistoryModal.results.length === 0 ? (
                <div className="flex items-center justify-center h-full min-h-[340px]">
                  <span className="text-gray-500">No speed test locations with GPS for this device.</span>
                </div>
              ) : (
                <MapContainer
                  center={[
                    speedTestHistoryModal.results[0].location_latitude,
                    speedTestHistoryModal.results[0].location_longitude,
                  ]}
                  zoom={15}
                  scrollWheelZoom
                  className="w-full h-full rounded-b-lg"
                  style={{ minHeight: 380 }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <SpeedTestHistoryFitBounds results={speedTestHistoryModal.results} />
                  {speedTestHistoryModal.results.map((r, idx) => {
                    const isLatest = idx === 0;
                    const isWifi = (r.connection_type || '').toLowerCase().includes('wifi');
                    const fillColor = isLatest ? '#2563eb' : isWifi ? '#22c55e' : '#f59e0b';
                    const color = isLatest ? '#1d4ed8' : isWifi ? '#16a34a' : '#d97706';
                    const radius = isLatest ? 12 : 7;
                    const weight = isLatest ? 3 : 1.5;
                    return (
                      <CircleMarker
                        key={`${r.timestamp}-${r.location_latitude}-${r.location_longitude}-${idx}`}
                        center={[r.location_latitude, r.location_longitude]}
                        radius={radius}
                        pathOptions={{ fillColor, color, fillOpacity: 0.8, weight }}
                      >
                        <Popup>
                          <div className="text-sm">
                            <div className="font-medium">{isLatest ? 'Latest' : 'Past'} – {r.connection_type || 'N/A'}</div>
                            <div><strong>Upload:</strong> {r.upload_mbps != null ? `${Number(r.upload_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                            <div><strong>Download:</strong> {r.download_mbps != null ? `${Number(r.download_mbps).toFixed(1)} Mbps` : 'N/A'}</div>
                            <div><strong>Signal:</strong> {r.signal_rsrp_dbm != null ? `${r.signal_rsrp_dbm} dBm` : 'N/A'}</div>
                            <div className="text-xs text-gray-500 mt-1">{r.timestamp ? new Date(r.timestamp).toLocaleString() : ''}</div>
                          </div>
                        </Popup>
                      </CircleMarker>
                    );
                  })}
                </MapContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Speed Test Results Modal */}
      {speedTestResultsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSpeedTestResultsModal({ isOpen: false, androidId: null, deviceFriendlyName: null, results: null, schedule: null, loading: false, status: null, error: null })}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-blue-600">Speed Test</h2>
                {speedTestResultsModal.deviceFriendlyName && (
                  <div className="text-sm text-gray-600 mt-1">{speedTestResultsModal.deviceFriendlyName}</div>
                )}
              </div>
              <button
                onClick={() => setSpeedTestResultsModal({ isOpen: false, androidId: null, deviceFriendlyName: null, results: null, schedule: null, loading: false, status: null, error: null })}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            {/* Loading State - dynamic step messages */}
            {speedTestResultsModal.loading && !speedTestResultsModal.results && (
              <div className="space-y-4">
                {/* Step indicator */}
                <div className="flex items-center justify-between gap-1 text-xs">
                  {[
                    { key: 'request', label: 'Request sent', statusVal: 'requesting' },
                    { key: 'wait', label: 'Waiting for device', statusVal: 'pending' },
                    { key: 'run', label: 'Running test', statusVal: 'acknowledged' },
                    { key: 'done', label: 'Done', statusVal: 'completed' },
                  ].map((s, idx) => {
                    const stepOrder = { requesting: 1, pending: 2, acknowledged: 3, completed: 4 };
                    const current = stepOrder[speedTestResultsModal.status] || 0;
                    const active = current >= idx + 1;
                    return (
                      <div key={s.key} className="flex flex-1 items-center">
                        <div className={`flex flex-col items-center flex-1 ${active ? 'text-blue-600' : 'text-gray-400'}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium border-2 ${active ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
                            {active ? (idx + 1 === current && speedTestResultsModal.status !== 'completed' ? (
                              <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                            ) : (
                              '✓'
                            )) : idx + 1}
                          </div>
                          <span className="mt-1 truncate max-w-full">{s.label}</span>
                        </div>
                        {idx < 3 && <div className={`flex-shrink-0 w-4 h-0.5 ${active ? 'bg-blue-200' : 'bg-gray-200'}`} />}
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-col items-center justify-center py-4 border-t border-gray-100">
                  <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-600 border-t-transparent mb-3"></div>
                  <div className="text-lg font-semibold text-gray-800">
                    {speedTestResultsModal.status === 'requesting' && 'Sending request...'}
                    {speedTestResultsModal.status === 'pending' && 'Waiting for device to pick up command'}
                    {speedTestResultsModal.status === 'acknowledged' && 'Device is running speed test'}
                    {!speedTestResultsModal.status && 'Fetching results...'}
                  </div>
                  <div className="text-sm text-gray-500 mt-1 max-w-sm text-center">
                    {speedTestResultsModal.status === 'requesting' && 'Command will be queued for the device.'}
                    {speedTestResultsModal.status === 'pending' && 'Device checks for commands every ~30 seconds. Ensure it is online.'}
                    {speedTestResultsModal.status === 'acknowledged' && 'Measuring download, upload and latency. This may take 30–60 seconds.'}
                    {!speedTestResultsModal.status && 'Almost done...'}
                  </div>
                </div>
              </div>
            )}
            
            {/* Error State */}
            {speedTestResultsModal.error && !speedTestResultsModal.loading && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-red-600 text-2xl">✗</span>
                    <div>
                      <div className="font-semibold text-red-800">Speed Test Failed</div>
                      <div className="text-sm text-red-600 mt-1">{speedTestResultsModal.error}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Results State */}
            {speedTestResultsModal.results && !speedTestResultsModal.loading && (
              <div className="space-y-4">
                {/* Connection Type and Carrier - always show; N/A when unknown */}
                <div className="bg-gray-50 p-3 rounded border">
                  <div className="text-xs text-gray-600 mb-1">Connection</div>
                  <div className="font-semibold text-gray-800">
                    {speedTestResultsModal.results.connection_type || speedTestResultsModal.results.carrier_name || 'N/A'}
                    {speedTestResultsModal.results.carrier_name && 
                     speedTestResultsModal.results.connection_type && 
                     speedTestResultsModal.results.carrier_name !== speedTestResultsModal.results.connection_type && (
                      <span className="text-gray-600 ml-2">• {speedTestResultsModal.results.carrier_name}</span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-3 rounded">
                    <div className="text-xs text-gray-600 mb-1">Download</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {speedTestResultsModal.results.download_mbps?.toFixed(2) || '0.00'} <span className="text-sm">Mbps</span>
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <div className="text-xs text-gray-600 mb-1">Upload</div>
                    <div className="text-2xl font-bold text-green-600">
                      {speedTestResultsModal.results.upload_mbps?.toFixed(2) || '0.00'} <span className="text-sm">Mbps</span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-gray-600">Ping</div>
                    <div className="font-semibold">{speedTestResultsModal.results.ping_ms?.toFixed(0) || '0'} ms</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Jitter</div>
                    <div className="font-semibold">{speedTestResultsModal.results.jitter_ms?.toFixed(1) || '0.0'} ms</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Loss</div>
                    <div className="font-semibold">{speedTestResultsModal.results.packet_loss_percent?.toFixed(1) || '0.0'}%</div>
                  </div>
                </div>
                
                {speedTestResultsModal.results.timestamp && (
                  <div className="text-xs text-gray-500 pt-2 border-t">
                    Test completed: {formatTimestampCST(speedTestResultsModal.results.timestamp)}
                  </div>
                )}
              </div>
            )}
            
            {speedTestResultsModal.schedule && speedTestResultsModal.schedule.next_speed_test_time && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-sm text-gray-600 mb-1">Next Scheduled Speed Test</div>
                <div className="font-semibold text-blue-600">
                  {formatTimestampCST(speedTestResultsModal.schedule.next_speed_test_time)}
                </div>
                {speedTestResultsModal.schedule.scheduled_time && (
                  <div className="text-xs text-gray-500 mt-1">
                    Scheduled time: {speedTestResultsModal.schedule.scheduled_time}
                  </div>
                )}
              </div>
            )}
            
            {speedTestResultsModal.schedule && !speedTestResultsModal.schedule.next_speed_test_time && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-sm text-gray-500">No scheduled speed test found</div>
              </div>
            )}
            
            {/* Close button - only show if not loading or if there's an error */}
            {(!speedTestResultsModal.loading || speedTestResultsModal.error) && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSpeedTestResultsModal({ isOpen: false, androidId: null, deviceFriendlyName: null, results: null, schedule: null, loading: false, status: null, error: null })}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

